import { quoteStringDoubleCurlyHtml, quoteStringDoubleVertical } from "../utils/quoteString";

export const hashAlgorithms = ["SHA-1", "SHA-256", "SHA-384", "SHA-512"] as const;
export type HashAlgorithm = (typeof hashAlgorithms)[number];

export interface EncodingStepBase {
  encodingType: 'hash' | 'base64' | 'hex' | 'removeSpaces' | 'lettersOnly' | 'alphanumericOnly' | 'asciiLettersOnly' | 'digitsOnly' | 'lowercase' | 'uppercase' | 'urlEncode';
}

export interface HashEncodingStep extends EncodingStepBase {
  encodingType: 'hash';
  subtype: HashAlgorithm;
}

export type Base64EncodingSubtype = "Full" | "AlignedStart" | "RemovingFirstTwoBits" | "RemovingFirstFourBits";

export interface Base64EncodingStep extends EncodingStepBase {
  encodingType: 'base64';
  subtype: Base64EncodingSubtype;
}

export interface RemoveSpacesEncodingStep extends EncodingStepBase {
  encodingType: 'removeSpaces';
}

export interface LettersOnlyEncodingStep extends EncodingStepBase {
  encodingType: 'lettersOnly';
}

export interface AlphanumericOnlyEncodingStep extends EncodingStepBase {
  encodingType: 'alphanumericOnly';
}

export interface AsciiLettersOnlyEncodingStep extends EncodingStepBase {
  encodingType: 'asciiLettersOnly';
}

export interface DigitsOnlyEncodingStep extends EncodingStepBase {
  encodingType: 'digitsOnly';
}

export interface HexEncodingStep extends EncodingStepBase {
  encodingType: 'hex';
}

export interface LowercaseEncodingStep extends EncodingStepBase {
  encodingType: 'lowercase';
}

export interface UppercaseEncodingStep extends EncodingStepBase {
  encodingType: 'uppercase';
}

export interface UrlEncodingStep extends EncodingStepBase {
  encodingType: 'urlEncode';
}

export type EncodingStep =
  HashEncodingStep |
  Base64EncodingStep |
  HexEncodingStep |
  RemoveSpacesEncodingStep |
  LettersOnlyEncodingStep |
  AlphanumericOnlyEncodingStep |
  AsciiLettersOnlyEncodingStep |
  DigitsOnlyEncodingStep |
  LowercaseEncodingStep |
  UppercaseEncodingStep |
  UrlEncodingStep;

const hashAlgorithmLc = {
  "SHA-1": "sha1",
  "SHA-256": "sha256",
  "SHA-384": "sha384",
  "SHA-512": "sha512"
  } as const satisfies Record<HashAlgorithm, string>;

type EncodingFunctionString = typeof hashAlgorithmLc[HashAlgorithm] | Exclude<EncodingStepBase["encodingType"], "hash">;
export const describeEncodingStepUsingFunctionNotation = (
  step: EncodingStep,
  value: string,
  fnNotation: (name: EncodingFunctionString, value: string) => string = (name, value) => `${name}(${value})`
) => {
  switch (step.encodingType) {
    case 'hash': return fnNotation(hashAlgorithmLc[step.subtype], value);
    case 'base64': {switch (step.subtype) {
      case 'Full':
      case 'AlignedStart': return fnNotation('base64', `${value}`);
      case 'RemovingFirstTwoBits': return fnNotation('base64', `${value}<<2`);
      case 'RemovingFirstFourBits': return  fnNotation('base64', `${value}<<4`);
    }}
    default:
      return fnNotation(step.encodingType, value);
  }
}

export const describeEncodedSearchTermUsingFunctionNotationRaw = <T extends string>(steps: EncodingStep[] | undefined, rawValue: string) =>
  (steps ?? []).reduce(
    (encoding, step) => describeEncodingStepUsingFunctionNotation(step, encoding), rawValue
  );

export const describeEncodedSearchTermUsingFunctionNotationText = (steps: EncodingStep[] | undefined, value: string) =>
  describeEncodedSearchTermUsingFunctionNotationRaw(steps, quoteStringDoubleVertical(value));

export const describeEncodedSearchTermUsingFunctionNotationHtml = (steps: EncodingStep[] | undefined, value: string) =>
  describeEncodedSearchTermUsingFunctionNotationRaw(steps, quoteStringDoubleCurlyHtml(value));



export interface EncodedSearchTerm {
  originalSearchTerm: string;
  encodedSearchTerm: string;
  encodingSteps: EncodingStep[];
}
